import React, { useEffect, useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
import { ThemeName } from '@/utils/storage';

interface PerfectDayCelebration3DProps {
  theme: ThemeName;
  onComplete?: () => void;
}

interface CelebrationTheme {
  title: string;
  message: string;
  hero: string;
  friends: string[];
  backdrop: string;
  glow: string;
}

const celebrationThemes: Record<ThemeName, CelebrationTheme> = {
  robots: {
    title: 'SYSTEM: AWESOME!',
    message: 'Every mission is complete!',
    hero: '🤖',
    friends: ['⚙️', '🔋', '💫', '🛸'],
    backdrop: 'linear-gradient(145deg, #020617 0%, #0c4a6e 50%, #172554 100%)',
    glow: '#22d3ee',
  },
  unicorn: {
    title: 'MAGICAL PERFECT DAY!',
    message: 'You made every star sparkle!',
    hero: '🦄',
    friends: ['🌈', '💖', '🦋', '💎'],
    backdrop: 'linear-gradient(145deg, #4a044e 0%, #86198f 48%, #312e81 100%)',
    glow: '#f0abfc',
  },
  space: {
    title: 'MISSION COMPLETE!',
    message: 'You collected a whole galaxy of stars!',
    hero: '🚀',
    friends: ['🪐', '🌎', '👽', '🌙'],
    backdrop: 'linear-gradient(145deg, #020617 0%, #312e81 48%, #172554 100%)',
    glow: '#a5b4fc',
  },
  ocean: {
    title: 'WAVE OF AWESOME!',
    message: 'You made a perfect-day splash!',
    hero: '🐬',
    friends: ['🐠', '🐙', '🐚', '🪸'],
    backdrop: 'linear-gradient(145deg, #083344 0%, #0e7490 48%, #164e63 100%)',
    glow: '#67e8f9',
  },
  forest: {
    title: 'WILDLY AMAZING!',
    message: 'Every task is done—way to grow!',
    hero: '🦉',
    friends: ['🦊', '🐿️', '🍄', '🌳'],
    backdrop: 'linear-gradient(145deg, #052e16 0%, #166534 50%, #365314 100%)',
    glow: '#bef264',
  },
  sunset: {
    title: 'GOLDEN DAY!',
    message: 'You finished everything under the sun!',
    hero: '🌞',
    friends: ['🌴', '🦩', '🌺', '🦜'],
    backdrop: 'linear-gradient(145deg, #4c0519 0%, #c2410c 50%, #7e22ce 100%)',
    glow: '#fde68a',
  },
  volcano: {
    title: 'ERUPTION OF AWESOME!',
    message: 'Your perfect day is on fire!',
    hero: '🌋',
    friends: ['🔥', '💎', '🦎', '☄️'],
    backdrop: 'linear-gradient(145deg, #180502 0%, #7f1d1d 52%, #431407 100%)',
    glow: '#fdba74',
  },
  trucks: {
    title: 'CHAMPION FINISH!',
    message: 'You crushed every task!',
    hero: '🏎️',
    friends: ['🏁', '🏆', '⚡', '🛞'],
    backdrop: 'linear-gradient(145deg, #0f172a 0%, #365314 50%, #1c1917 100%)',
    glow: '#bef264',
  },
  ninja: {
    title: 'LEGENDARY FOCUS!',
    message: 'Every challenge has been conquered!',
    hero: '🥷',
    friends: ['🐉', '⛩️', '🌙', '⚡'],
    backdrop: 'linear-gradient(145deg, #020617 0%, #450a0a 50%, #18181b 100%)',
    glow: '#fca5a5',
  },
  spongebob: {
    title: 'UNDERSEA SUPERSTAR!',
    message: 'Every task is shipshape!',
    hero: '🧽',
    friends: ['🐠', '🫧', '⭐', '🐚'],
    backdrop: 'linear-gradient(145deg, #164e63 0%, #0369a1 50%, #0f766e 100%)',
    glow: '#fde047',
  },
  dinosaurs: {
    title: 'ROAR-SOME DAY!',
    message: 'You stomped every task!',
    hero: '🦖',
    friends: ['🦕', '🥚', '🌿', '☄️'],
    backdrop: 'linear-gradient(145deg, #052e16 0%, #15803d 48%, #713f12 100%)',
    glow: '#bef264',
  },
  candy: {
    title: 'SWEET VICTORY!',
    message: 'You made today extra amazing!',
    hero: '🍭',
    friends: ['🍬', '🧁', '🍩', '🌈'],
    backdrop: 'linear-gradient(145deg, #831843 0%, #be185d 48%, #6d28d9 100%)',
    glow: '#fbcfe8',
  },
};

const cubeFaces = [
  'translateZ(64px)',
  'rotateY(180deg) translateZ(64px)',
  'rotateY(90deg) translateZ(64px)',
  'rotateY(-90deg) translateZ(64px)',
  'rotateX(90deg) translateZ(64px)',
  'rotateX(-90deg) translateZ(64px)',
];

const PerfectDayCelebration3D: React.FC<PerfectDayCelebration3DProps> = ({
  theme,
  onComplete,
}) => {
  const reducedMotion = useReducedMotion();
  const design = celebrationThemes[theme];
  const particles = useMemo(
    () =>
      Array.from({ length: reducedMotion ? 12 : 34 }, (_, index) => ({
        symbol: index % 3 === 0 ? '🌟' : index % 3 === 1 ? '✨' : '⭐',
        left: (index * 29) % 100,
        delay: (index % 9) * 0.12,
        depth: (index % 7) * 24 - 72,
        drift: ((index * 47) % 180) - 90,
      })),
    [reducedMotion]
  );

  useEffect(() => {
    const dismissTimer = window.setTimeout(() => onComplete?.(), reducedMotion ? 9000 : 8000);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onComplete?.();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(dismissTimer);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onComplete, reducedMotion]);

  return (
    <div
      className="perfect-day-celebration fixed inset-0 z-[100] flex items-center justify-center overflow-hidden text-white"
      style={{ background: design.backdrop }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="perfect-day-title"
      aria-describedby="perfect-day-message"
    >
      <button
        type="button"
        onClick={onComplete}
        className="absolute right-4 top-4 z-50 flex min-h-11 min-w-11 items-center justify-center rounded-full border-2 border-white/80 bg-black/60 text-white shadow-lg transition hover:bg-black/80 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white"
        aria-label="Close celebration"
      >
        <X aria-hidden="true" />
      </button>

      <div
        className="absolute inset-0 opacity-45"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 20%, white 0 2px, transparent 3px), radial-gradient(circle at 75% 35%, white 0 1px, transparent 2px)',
          backgroundSize: '90px 90px, 64px 64px',
        }}
        aria-hidden="true"
      />

      <div className="absolute inset-0 [perspective:900px]" aria-hidden="true">
        {particles.map((particle, index) => (
          <motion.span
            key={`${particle.symbol}-${index}`}
            initial={{ y: '110vh', opacity: 0, rotateZ: 0 }}
            animate={
              reducedMotion
                ? { opacity: 0.8 }
                : {
                    y: '-20vh',
                    x: particle.drift,
                    opacity: [0, 1, 1, 0],
                    rotateZ: 540,
                  }
            }
            transition={{
              delay: particle.delay,
              duration: 4.5 + (index % 4),
              repeat: reducedMotion ? 0 : Infinity,
              ease: 'linear',
            }}
            className="absolute bottom-0 text-2xl sm:text-4xl"
            style={{
              left: `${particle.left}%`,
              transform: `translateZ(${particle.depth}px)`,
              filter: `drop-shadow(0 0 8px ${design.glow})`,
            }}
          >
            {particle.symbol}
          </motion.span>
        ))}
      </div>

      <div className="relative flex h-full w-full max-w-5xl flex-col items-center justify-center px-5 [perspective:1000px]">
        <motion.div
          initial={{ opacity: 0, y: -40, scale: 0.75 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: 'spring', damping: 12, stiffness: 120 }}
          className="absolute top-[9%] z-30 max-w-4xl text-center"
        >
          <h1
            id="perfect-day-title"
            className="text-balance text-4xl font-black tracking-wide text-white sm:text-6xl md:text-7xl"
            style={{ textShadow: '0 3px 0 rgba(0,0,0,.8), 0 0 28px rgba(255,255,255,.45)' }}
          >
            {design.title}
          </h1>
          <p
            id="perfect-day-message"
            className="mx-auto mt-3 w-fit rounded-full border border-white/40 bg-black/55 px-5 py-2 text-lg font-bold text-white shadow-xl sm:text-2xl"
          >
            {design.message}
          </p>
        </motion.div>

        <motion.div
          animate={reducedMotion ? {} : { rotateX: [8, -4, 8], rotateY: [-8, 8, -8] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          className="relative mt-24 h-72 w-72 [transform-style:preserve-3d] sm:h-96 sm:w-96"
          style={{ filter: `drop-shadow(0 30px 35px rgba(0,0,0,.55)) drop-shadow(0 0 32px ${design.glow})` }}
          aria-hidden="true"
        >
          <motion.div
            animate={reducedMotion ? {} : { rotateY: 360, rotateX: [0, 12, 0] }}
            transition={{ rotateY: { duration: 8, repeat: Infinity, ease: 'linear' }, rotateX: { duration: 4, repeat: Infinity } }}
            className="absolute left-1/2 top-1/2 h-32 w-32 -translate-x-1/2 -translate-y-1/2 [transform-style:preserve-3d]"
          >
            {cubeFaces.map((transform, index) => (
              <div
                key={transform}
                className="absolute inset-0 flex items-center justify-center rounded-3xl border-4 border-white/70 bg-white/20 text-7xl shadow-[inset_0_0_30px_rgba(255,255,255,.4)] backdrop-blur-sm"
                style={{ transform, backfaceVisibility: 'hidden' }}
              >
                {index < 2 ? design.hero : '⭐'}
              </div>
            ))}
          </motion.div>

          {design.friends.map((friend, index) => {
            const angle = index * 90;
            return (
              <motion.div
                key={friend}
                animate={reducedMotion ? {} : { rotateY: [angle, angle + 360] }}
                transition={{ duration: 10 + index, repeat: Infinity, ease: 'linear' }}
                className="absolute inset-0 [transform-style:preserve-3d]"
              >
                <motion.span
                  animate={reducedMotion ? {} : { rotateY: [0, -360], y: [0, -12, 0] }}
                  transition={{ duration: 10 + index, repeat: Infinity, ease: 'linear' }}
                  className="absolute left-1/2 top-1/2 text-5xl sm:text-7xl"
                  style={{ transform: `translate3d(-50%, -50%, 0) translateZ(${125 + index * 20}px)` }}
                >
                  {friend}
                </motion.span>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: [0, 1.25, 1], opacity: 1 }}
          transition={{ delay: reducedMotion ? 0 : 1.1, duration: 0.8 }}
          className="absolute bottom-[8%] z-30 rounded-2xl border-2 border-yellow-200 bg-slate-950/80 px-6 py-3 text-center shadow-2xl"
        >
          <p className="text-xl font-black text-yellow-200 sm:text-3xl">ALL DAILY STARS COLLECTED!</p>
        </motion.div>
      </div>
    </div>
  );
};

export default PerfectDayCelebration3D;
